import { useState, useEffect } from 'react';
import { getBusinessMetrics } from '../../lib/api';
import { Card } from '../ui/card';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from 'recharts';
import { DollarSign, TrendingUp, Users, Target, Award, AlertCircle } from 'lucide-react';

const COLORS = ['#3b82f6', '#8b5cf6', '#06b6d4', '#f59e0b', '#ef4444', '#10b981', '#ec4899', '#6366f1', '#14b8a6'];

const STAGE_LABELS = {
  new_lead: 'New Lead',
  contacted: 'Contacted',
  demo_scheduled: 'Demo Sched.',
  demo_completed: 'Demo Done',
  follow_up: 'Follow Up',
  proposal: 'Proposal',
  contract: 'Contract',
  closed_won: 'Won',
  closed_lost: 'Lost',
};

function MetricCard({ icon: Icon, label, value, subtitle, color = 'blue' }) {
  const colors = {
    blue: 'bg-blue-500/10 text-blue-400',
    green: 'bg-emerald-500/10 text-emerald-400',
    purple: 'bg-purple-500/10 text-purple-400',
    orange: 'bg-orange-500/10 text-orange-400',
    red: 'bg-red-500/10 text-red-400',
    teal: 'bg-teal-500/10 text-teal-600',
  };
  return (
    <Card className="p-5">
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-xs text-zinc-500 uppercase tracking-wide">{label}</p>
          <p className="text-2xl font-semibold text-zinc-100 mt-0.5">{value}</p>
          {subtitle && <p className="text-xs text-zinc-400 mt-1">{subtitle}</p>}
        </div>
      </div>
    </Card>
  );
}

export default function MetricsPage() {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getBusinessMetrics()
      .then((res) => setMetrics(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-900/50 border-t-blue-400 rounded-full animate-spin" />
      </div>
    );
  }

  if (!metrics) return null;

  const fmt = (n) => {
    if (n >= 1000000) return `$${(n / 1000000).toFixed(2)}M`;
    if (n >= 1000) return `$${(n / 1000).toFixed(1)}K`;
    return `$${n.toFixed(0)}`;
  };

  const stageData = metrics.stage_counts
    ? Object.entries(metrics.stage_counts).map(([stage, count]) => ({
        name: STAGE_LABELS[stage] || stage,
        count,
      }))
    : [];

  const funnelData = stageData.filter((s) => s.count > 0);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-zinc-100">Business Metrics</h1>
        <p className="text-sm text-zinc-500 mt-1">Key investor &amp; company insights</p>
      </div>

      {/* Revenue Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <MetricCard icon={DollarSign} label="Monthly Recurring Revenue" value={fmt(metrics.mrr)} subtitle="Total ARR / 12" color="green" />
        <MetricCard icon={DollarSign} label="Annual Recurring Revenue" value={fmt(metrics.arr)} subtitle="Sum of won deal values" color="blue" />
        <MetricCard icon={TrendingUp} label="Customer Lifetime Value" value={fmt(metrics.ltv)} subtitle="Avg deal × 24 months" color="purple" />
        <MetricCard icon={DollarSign} label="Total Revenue" value={fmt(metrics.total_revenue)} color="teal" />
        <MetricCard icon={DollarSign} label="Pipeline Value" value={fmt(metrics.pipeline_value)} subtitle="Active deals in pipeline" color="orange" />
        <MetricCard icon={DollarSign} label="Avg Deal Size" value={fmt(metrics.avg_deal_size)} color="blue" />
      </div>

      {/* Conversion Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard icon={Users} label="Total Leads" value={metrics.total_leads} color="blue" />
        <MetricCard icon={Award} label="Won Deals" value={metrics.won_deals} color="green" />
        <MetricCard icon={AlertCircle} label="Lost Deals" value={metrics.lost_deals} color="red" />
        <MetricCard icon={Target} label="Win Rate" value={`${metrics.conversion_rate}%`} subtitle={`Loss rate: ${metrics.loss_rate}%`} color="purple" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pipeline Funnel */}
        {funnelData.length > 0 && (
          <Card className="p-5">
            <h2 className="text-base font-medium text-zinc-100 mb-4">Pipeline Funnel</h2>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={funnelData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-25} textAnchor="end" height={60} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                    {funnelData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        )}

        {/* Monthly Leads */}
        {metrics.monthly_leads?.length > 0 && (
          <Card className="p-5">
            <h2 className="text-base font-medium text-zinc-100 mb-4">Monthly Lead Acquisition</h2>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={metrics.monthly_leads}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        )}
      </div>

      {/* Investor Summary Table */}
      <Card className="p-5">
        <h2 className="text-base font-medium text-zinc-100 mb-4">Investor Summary</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-800/50">
                <th className="text-left px-4 py-2 font-medium text-zinc-500">Metric</th>
                <th className="text-right px-4 py-2 font-medium text-zinc-500">Value</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['MRR', fmt(metrics.mrr)],
                ['ARR', fmt(metrics.arr)],
                ['LTV', fmt(metrics.ltv)],
                ['Total Revenue', fmt(metrics.total_revenue)],
                ['Pipeline Value', fmt(metrics.pipeline_value)],
                ['Avg Deal Size', fmt(metrics.avg_deal_size)],
                ['Total Leads', metrics.total_leads],
                ['Won Deals', metrics.won_deals],
                ['Win Rate', `${metrics.conversion_rate}%`],
                ['Loss Rate', `${metrics.loss_rate}%`],
              ].map(([label, val]) => (
                <tr key={label} className="border-b border-zinc-800/50">
                  <td className="px-4 py-2.5 text-zinc-300">{label}</td>
                  <td className="px-4 py-2.5 text-right font-medium text-zinc-100">{val}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
